import {useCallback, useMemo} from 'react';
import {Concept} from 'types';
import type {EditStatus} from 'scenes/updateMetadata/types';
import type {Data, Schema, Valid} from 'schemaDefinition/types';
import type {CodeList, CodeListId} from 'services/codelists/types';
import type {EditMode, ManifestationStatus} from 'services/metadata/types';
import type {CodelistRestrictor} from 'services/codelists';
import {
  metadataEditSetEditMode,
  useEditMode,
  useEditStatus,
  useExpressionImportProps,
  useExpressionLocalCodelistMap,
  useExpressionValidation,
  useStatusAndShowError,
} from 'services/metadata';
import {useAppDispatch} from 'store/hooks';
import {
  useExpressionCodelistRestrictor,
  useRestrictedCodelistsForSchema,
} from 'schemas';
import {useModifiedSchema} from 'schemas/hooks/useModifiedSchema';
import type {ExpressionCardCoreProps} from '../types';

export const useExpressionEditStateCore = ({
  expressionId,
  stableExpression,
  // Given readonly prop, i.e. edit mode not available when readonly only
  readonly: readonlyOnly,
}: ExpressionCardCoreProps): {
  codelistRestrictor: CodelistRestrictor;
  getLocalCodelist: (id: CodeListId) => CodeList | undefined;
  /** Aggregated status, undefined when not yet loaded */
  expressionStatus: ManifestationStatus | undefined;
  relatedScope: Data;
  schema: Schema;
  readonly: boolean;
  editStatus: EditStatus;
  valid: Valid | undefined;
  showErrors: boolean;
  handleEdit: () => void;
  handleCancelEdit: () => void;
} => {
  const dispatch = useAppDispatch();
  const {activeStatus: expressionStatus, showError} =
    useStatusAndShowError(expressionId);
  const {hasDataChanged} = useEditStatus(expressionId);
  const mode = useEditMode(expressionId, 'root', 'readonly');

  const relatedScope = useExpressionImportProps(expressionId);

  const codelistRestrictor = useExpressionCodelistRestrictor(expressionId);

  const codelistMap = useRestrictedCodelistsForSchema(
    codelistRestrictor,
    Concept.expression,
  );

  const localCodelistMap = useExpressionLocalCodelistMap(expressionId);

  const combinedCodelistMap = useMemo(
    () => ({
      ...codelistMap,
      ...localCodelistMap,
    }),
    [codelistMap, localCodelistMap],
  );

  const schema = useModifiedSchema(
    stableExpression,
    relatedScope.work.type,
    Concept.expression,
    expressionStatus,
  );

  const valid = useExpressionValidation(
    expressionId,
    relatedScope,
    showError,
    schema,
    combinedCodelistMap,
  );

  const setMode = useCallback(
    (mode: EditMode) => {
      dispatch(
        metadataEditSetEditMode({
          id: expressionId,
          key: 'root',
          mode,
        }),
      );
    },
    [dispatch, expressionId],
  );

  const handleEdit = useCallback(() => {
    if (readonlyOnly) {
      return;
    }

    setMode('edit');
  }, [readonlyOnly, setMode]);

  const handleCancelEdit = useCallback(() => {
    if (hasDataChanged) {
      return;
    }

    setMode('readonly');
  }, [hasDataChanged, setMode]);

  return useMemo(
    () => ({
      codelistRestrictor,
      getLocalCodelist: id => localCodelistMap[id],
      expressionStatus,
      relatedScope,
      schema,
      editStatus: hasDataChanged ? 'Changed' : 'Unchanged',
      readonly: mode === 'readonly',
      valid,
      showErrors: showError || showError,
      handleEdit,
      handleCancelEdit,
    }),
    [
      codelistRestrictor,
      expressionStatus,
      relatedScope,
      schema,
      hasDataChanged,
      mode,
      valid,
      showError,
      handleEdit,
      handleCancelEdit,
      localCodelistMap,
    ],
  );
};
